import React, { useCallback, useEffect, useState } from "react";
import { caApi } from "../../../../../../backend_urls/caApi";

const getEmptyForm = () => ({
    title: "",
    description: "",
    employee_id: "",
    priority: "medium",
    due_date: "",
});

const EMPTY_OPTIONS = { employees: [] };

const TaskCreate = ({
    isOpen = false,
    onClose,
    onSuccess,
}) => {
    const [form, setForm] = useState(getEmptyForm());

    const [options, setOptions] = useState(EMPTY_OPTIONS);

    const [loadingOptions, setLoadingOptions] = useState(false);
    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    // ========================================================
    // RESET FORM
    // ========================================================

    const resetForm = useCallback(() => {
        setForm(getEmptyForm());
        setError("");
        setFieldErrors({});
    }, []);

    // ========================================================
    // LOAD EMPLOYEES
    // ========================================================

    const loadOptions = useCallback(async () => {
        try {
            setLoadingOptions(true);
            setError("");

            const response = await caApi.getTaskCreateOptions();

            const responseData = response?.data ?? response;

            setOptions({
                employees: Array.isArray(responseData?.employees)
                    ? responseData.employees
                    : [],
            });
        } catch (err) {
            console.error("Failed to load task creation options:", err);

            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Unable to load employees."
            );
        } finally { setLoadingOptions(false); }
    }, []);

    // ========================================================
    // WHEN DRAWER OPENS
    // ========================================================

    useEffect(() => {
        if (!isOpen) { return; }

        resetForm();
        loadOptions();
    }, [isOpen, loadOptions, resetForm]);

    // ========================================================
    // FORM CHANGE
    // ========================================================

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((previous) => ({ ...previous, [name]: value }));
        setFieldErrors((previous) => ({ ...previous, [name]: "" }));
        setError("");
    };

    // ========================================================
    // VALIDATION
    // ========================================================

    const validateForm = () => {
        const errors = {};

        if (!form.title.trim()) {
            errors.title = "Task title is required.";
        }

        if (!form.employee_id) {
            errors.employee_id = "Please assign an employee.";
        }

        if (!form.priority) {
            errors.priority = "Please select a priority.";
        }

        if (!form.due_date) {
            errors.due_date = "Due date is required.";
        }

        setFieldErrors(errors);

        return Object.keys(errors).length === 0;
    };

    // ========================================================
    // CREATE TASK
    // ========================================================

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (loading) { return; }
        if (!validateForm()) { return; }

        try {
            setLoading(true);
            setError("");

            const payload = {
                title: form.title.trim(),
                description: form.description.trim(),
                assigned_employees: [Number(form.employee_id)],
                priority: form.priority,
                due_date: form.due_date || null,
            };

            const response = await caApi.createTask(payload);

            const createdTask = response?.data ?? response;

            resetForm();

            if (onSuccess) { await onSuccess(createdTask); }
            onClose?.();
        } catch (err) {
            console.error("Failed to create task:", err);

            const responseData = err?.response?.data;

            let message = responseData?.detail || responseData?.message;
            let errors = {};

            if (
                responseData &&
                typeof responseData === "object"
            ) {
                Object.entries(responseData).forEach(
                    ([key, value]) => {
                        if (key === "detail" || key === "message") { return; }

                        if (Array.isArray(value)) {
                            errors[key] = value.join(" ");
                        } else if (typeof value === "string") {
                            errors[key] = value;
                        }
                    }
                );
            }

            if (Object.keys(errors).length > 0) { setFieldErrors(errors); }

            setError(
                message ||
                "Unable to create task. Please check the form."
            );
        } finally { setLoading(false); }
    };

    // ========================================================
    // CLOSE
    // ========================================================
    const handleClose = () => {
        if (loading) { return; }
        resetForm();
        onClose?.();
    };

    // ========================================================
    // DO NOT RENDER WHEN CLOSED
    // ========================================================
    if (!isOpen) { return null; }

    // ========================================================
    // RENDER
    // ========================================================
    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 23, 42, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: "20px",
            }}
        >
            <div
                style={{
                    width: "100%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    background: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
                }}
            >
                {/* ====================================================
                    HEADER
                ==================================================== */}

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "20px 24px",
                        borderBottom: "1px solid #e2e8f0",
                    }}
                >
                    <div>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: "20px",
                                fontWeight: 600,
                                color: "#0f172a",
                            }}
                        >
                            Create New Task
                        </h2>

                        <p
                            style={{
                                margin: "5px 0 0",
                                fontSize: "13px",
                                color: "#64748b",
                            }}
                        >
                            Create a task and assign it to an employee.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        style={{
                            border: "none",
                            background: "transparent",
                            fontSize: "24px",
                            lineHeight: 1,
                            color: "#64748b",
                            cursor: loading ? "not-allowed" : "pointer",
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* ====================================================
                    FORM
                ==================================================== */}

                <form onSubmit={handleSubmit}>
                    <div
                        style={{
                            padding: "24px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "18px",
                        }}
                    >
                        {/* GENERAL ERROR */}

                        {error && (
                            <div
                                style={{
                                    padding: "12px 14px",
                                    borderRadius: "8px",
                                    background: "#fef2f2",
                                    color: "#b91c1c",
                                    fontSize: "14px",
                                }}
                            >
                                {error}
                            </div>
                        )}

                        {/* TITLE */}

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "7px",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#334155",
                                }}
                            >
                                Task Title
                                <span
                                    style={{
                                        color: "#dc2626",
                                        marginLeft: "3px",
                                    }}
                                >
                                    *
                                </span>
                            </label>

                            <input
                                type="text"
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                placeholder="Enter task title"
                                disabled={loading}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "7px",
                                    border: fieldErrors.title
                                        ? "1px solid #dc2626"
                                        : "1px solid #cbd5e1",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />

                            {fieldErrors.title && (
                                <div
                                    style={{
                                        marginTop: "5px",
                                        fontSize: "12px",
                                        color: "#dc2626",
                                    }}
                                >
                                    {fieldErrors.title}
                                </div>
                            )}
                        </div>

                        {/* DESCRIPTION */}

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "7px",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#334155",
                                }}
                            >
                                Description
                            </label>

                            <textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Describe the task..."
                                rows={4}
                                disabled={loading}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "7px",
                                    border: "1px solid #cbd5e1",
                                    outline: "none",
                                    resize: "vertical",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>

                        {/* EMPLOYEE */}

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "7px",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#334155",
                                }}
                            >
                                Assign Employee
                                <span
                                    style={{
                                        color: "#dc2626",
                                        marginLeft: "3px",
                                    }}
                                >
                                    *
                                </span>
                            </label>

                            <select
                                name="employee_id"
                                value={form.employee_id}
                                onChange={handleChange}
                                disabled={ loading || loadingOptions }
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "7px",
                                    border: fieldErrors.employee_id
                                        ? "1px solid #dc2626"
                                        : "1px solid #cbd5e1",
                                    background: "#ffffff",
                                    boxSizing: "border-box",
                                }}
                            >
                                <option value="">
                                    {loadingOptions
                                        ? "Loading employees..."
                                        : "Select employee"}
                                </option>

                                {options.employees.map(
                                    (employee) => (
                                        <option
                                            key={employee.id}
                                            value={employee.id}
                                        >
                                            {employee.user_full_name ||
                                                employee.name ||
                                                employee.user_email ||
                                                `Employee #${employee.id}`}
                                        </option>
                                    )
                                )}
                            </select>

                            {fieldErrors.employee_id && (
                                <div
                                    style={{
                                        marginTop: "5px",
                                        fontSize: "12px",
                                        color: "#dc2626",
                                    }}
                                >
                                    {fieldErrors.employee_id}
                                </div>
                            )}
                        </div>

                        {/* PRIORITY */}

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "7px",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#334155",
                                }}
                            >
                                Priority
                                <span
                                    style={{
                                        color: "#dc2626",
                                        marginLeft: "3px",
                                    }}
                                >
                                    *
                                </span>
                            </label>

                            <select
                                name="priority"
                                value={form.priority}
                                onChange={handleChange}
                                disabled={loading}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "7px",
                                    border: fieldErrors.priority
                                        ? "1px solid #dc2626"
                                        : "1px solid #cbd5e1",
                                    background: "#ffffff",
                                    boxSizing: "border-box",
                                }}
                            >
                                <option value="low">
                                    Low
                                </option>

                                <option value="medium">
                                    Medium
                                </option>

                                <option value="high">
                                    High
                                </option>

                                <option value="critical">
                                    Critical
                                </option>
                            </select>

                            {fieldErrors.priority && (
                                <div
                                    style={{
                                        marginTop: "5px",
                                        fontSize: "12px",
                                        color: "#dc2626",
                                    }}
                                >
                                    {fieldErrors.priority}
                                </div>
                            )}
                        </div>

                        {/* DUE DATE */}

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "7px",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#334155",
                                }}
                            >
                                Due Date
                                <span
                                    style={{
                                        color: "#dc2626",
                                        marginLeft: "3px",
                                    }}
                                >
                                    *
                                </span>
                            </label>

                            <input
                                type="date"
                                name="due_date"
                                value={form.due_date}
                                onChange={handleChange}
                                disabled={loading}
                                style={{
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "7px",
                                    border: fieldErrors.due_date
                                        ? "1px solid #dc2626"
                                        : "1px solid #cbd5e1",
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />

                            {fieldErrors.due_date && (
                                <div
                                    style={{
                                        marginTop: "5px",
                                        fontSize: "12px",
                                        color: "#dc2626",
                                    }}
                                >
                                    {fieldErrors.due_date}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ====================================================
                        FOOTER
                    ==================================================== */}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            padding: "16px 24px",
                            borderTop: "1px solid #e2e8f0",
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            style={{
                                padding: "9px 16px",
                                borderRadius: "7px",
                                border: "1px solid #cbd5e1",
                                background: "#ffffff",
                                color: "#334155",
                                cursor: loading
                                    ? "not-allowed"
                                    : "pointer",
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={ loading || loadingOptions }
                            style={{
                                padding: "9px 18px",
                                borderRadius: "7px",
                                border: "none",
                                background: "#2563eb",
                                color: "#ffffff",
                                cursor:
                                    loading ||
                                    loadingOptions
                                        ? "not-allowed"
                                        : "pointer",
                                opacity:
                                    loading ||
                                    loadingOptions
                                        ? 0.7
                                        : 1,
                            }}
                        >
                            {loading ? "Creating..." : "Create Task"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskCreate;
